import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import type { SubscriptionBanner as Banner } from '@barbeiropro/contracts';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

const daysLeft = (iso: string | null) => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)) : null);

/** Avisos de teste, atraso, suspensão e cancelamento agendado. */
export function SubscriptionBanner() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ['subscription-banner'], queryFn: () => api.get<Banner>('/subscription/banner'), staleTime: 60_000, retry: false });
  const b = q.data;
  if (!b) return null;
  let tone: 'warn' | 'bad' | null = null;
  let text = '';
  if (b.organizationStatus === 'suspended' || b.status === 'suspended') {
    tone = 'bad';
    text = `Conta suspensa${b.suspendedReason ? ` (${b.suspendedReason})` : ''}. Você pode consultar e exportar seus dados, mas não criar ou alterar registros até regularizar.`;
  } else if (b.organizationStatus === 'pending_deletion') {
    tone = 'bad';
    text = 'A exclusão desta conta está agendada. Exporte seus dados e fale com o suporte para cancelar.';
  } else if (b.status === 'past_due') {
    tone = 'warn';
    text = 'Pagamento em atraso. Regularize para evitar a suspensão da conta.';
  } else if (b.status === 'grace') {
    tone = 'bad';
    text = `Pagamento em atraso: a conta será suspensa em ${b.graceUntil ? fmtDate(b.graceUntil, 'America/Sao_Paulo') : 'breve'}.`;
  } else if (b.status === 'trialing') {
    const d = daysLeft(b.trialEndsAt);
    if (d !== null && d <= 7) {
      tone = 'warn';
      text = d === 0 ? 'Seu período de teste termina hoje.' : `Seu período de teste termina em ${d} ${d === 1 ? 'dia' : 'dias'}.`;
    }
  } else if (b.cancelAtPeriodEnd) {
    tone = 'warn';
    text = `Cancelamento agendado para ${b.currentPeriodEnd ? fmtDate(b.currentPeriodEnd, 'America/Sao_Paulo') : 'o fim do período'}.`;
  }
  if (!tone) return null;
  return (
    <div role="status" className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm sm:px-6', tone === 'bad' ? 'border-destructive/30 bg-destructive/10 text-destructive' : 'border-brass/40 bg-brass/10 text-amber-ink')}>
      <TriangleAlert className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{text}</span>
      {can('settings.manage') && (
        <Link to="/app/configuracoes/assinatura" className="font-medium underline">
          Ver assinatura
        </Link>
      )}
    </div>
  );
}
